import React from "react";

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const formatMMSS = (totalSeconds) => {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const mm = String(Math.floor(s / 60));
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

// Motivational tiers based on goal completion percentage
const getMotivationTier = (ratio) => {
  if (ratio < 0.3) return {
    level: 1,
    title: "Getting Started",
    message: "Every minute counts! Keep going!",
    icon: "🌱",
    color: "from-blue-400 to-cyan-400",
    gradient: "bg-gradient-to-r from-blue-400 to-cyan-400",
  };
  if (ratio < 0.6) return {
    level: 2,
    title: "Building Momentum",
    message: "You're finding your rhythm!",
    icon: "🔥",
    color: "from-amber-400 to-orange-400",
    gradient: "bg-gradient-to-r from-amber-400 to-orange-400",
  };
  if (ratio < 1) return {
    level: 3,
    title: "Almost There!",
    message: "Just a little more to reach your goal!",
    icon: "🎯",
    color: "from-purple-400 to-pink-400",
    gradient: "bg-gradient-to-r from-purple-400 to-pink-400",
  };
  if (ratio < 1.5) return {
    level: 4,
    title: "Goal Achieved!",
    message: "Excellent work! You've hit your target!",
    icon: "🏆",
    color: "from-emerald-400 to-green-400",
    gradient: "bg-gradient-to-r from-emerald-400 to-green-400",
  };
  return {
    level: 5,
    title: "Above & Beyond!",
    message: "Outstanding! You're crushing it!",
    icon: "⭐",
    color: "from-violet-500 to-purple-500",
    gradient: "bg-gradient-to-r from-violet-500 to-purple-500",
  };
};

// Time-based achievement suggestions
const getTimeAchievements = (goalSeconds) => {
  const goalMinutes = Math.round(goalSeconds / 60);
  
  if (goalMinutes <= 10) return [
    { minutes: 5, label: "Good Start", icon: "👏" },
    { minutes: 10, label: "Excellent", icon: "✨" },
    { minutes: 15, label: "Amazing", icon: "🌟" },
  ];
  
  if (goalMinutes <= 20) return [
    { minutes: 10, label: "Solid Effort", icon: "👍" },
    { minutes: 15, label: "Good", icon: "👏" },
    { minutes: 20, label: "Excellent", icon: "✨" },
  ];
  
  return [
    { minutes: 15, label: "Good Start", icon: "👍" },
    { minutes: 20, label: "Solid", icon: "👏" },
    { minutes: 25, label: "Excellent", icon: "✨" },
    { minutes: 30, label: "Amazing", icon: "🌟" },
  ];
};

export default function ProgressModal({
  open,
  onClose,
  doneSeconds = 0,
  goalSeconds,
  onGoalMinus,
  onGoalPlus,
  minGoalSeconds = 5 * 60,
}) {
  if (!open) return null;

  const safeGoal = Math.max(1, Number(goalSeconds ?? minGoalSeconds));
  const safeDone = Math.max(0, Number(doneSeconds || 0));

  const ratio = safeDone / safeGoal;
  const pct = Math.round(ratio * 100);
  const barPct = clamp(Math.round(ratio * 100), 0, 100);
  
  const motivation = getMotivationTier(ratio);
  const achievements = getTimeAchievements(safeGoal);

  const minusDisabled =
    typeof onGoalMinus !== "function" || safeGoal <= minGoalSeconds;
  const plusDisabled = typeof onGoalPlus !== "function";

  return (
    <div className="fixed inset-0 z-[80]">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* modal */}
      <div className="absolute left-1/2 top-1/2 w-[92vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-white/30 bg-gradient-to-br from-white to-gray-50 p-6 shadow-2xl shadow-black/20">
        
        {/* Header with close button */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className={`rounded-xl p-2 ${motivation.gradient} bg-opacity-10`}>
                <span className="text-xl">{motivation.icon}</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Today's Progress</h3>
            </div>

            {/* Motivational message */}
            <div className="mt-2">
              <p className={`text-sm font-semibold ${motivation.color} bg-clip-text text-transparent`}>
                {motivation.title}
              </p>
              <p className="text-sm text-gray-600">{motivation.message}</p>
            </div>

            {/* Goal controls */}
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={onGoalMinus}
                disabled={minusDisabled}
                className={`rounded-xl border border-gray-200 bg-white px-3 py-2 text-gray-700 transition-all hover:bg-gray-50 hover:shadow-sm active:scale-95 ${
                  minusDisabled ? "opacity-40 cursor-not-allowed" : ""
                }`}
                aria-label="Decrease goal"
                title={
                  minusDisabled
                    ? `Minimum: ${formatMMSS(minGoalSeconds)}`
                    : "Decrease goal"
                }
              >
                <span className="text-lg font-bold">−</span>
              </button>

              <div className="rounded-xl border border-gray-200 bg-white px-4 py-2">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">{formatMMSS(safeGoal)}</span> goal
                </p>
              </div>

              <button
                type="button"
                onClick={onGoalPlus}
                disabled={plusDisabled}
                className={`rounded-xl border border-gray-200 bg-white px-3 py-2 text-gray-700 transition-all hover:bg-gray-50 hover:shadow-sm active:scale-95 ${
                  plusDisabled ? "opacity-40 cursor-not-allowed" : ""
                }`}
                aria-label="Increase goal"
                title={plusDisabled ? "" : "Increase goal"}
              >
                <span className="text-lg font-bold">+</span>
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-gray-200 bg-white p-2 text-gray-700 transition-all hover:bg-gray-50 hover:shadow-sm active:scale-95"
            aria-label="Close"
          >
            <span className="text-lg">✕</span>
          </button>
        </div>

        {/* Progress card */}
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-3xl font-extrabold text-gray-900">
                {formatMMSS(safeDone)}
              </div>
              <div className="mt-1 text-sm text-gray-500">Time practiced</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">
                {pct}%
              </div>
              <div className="text-sm font-semibold text-gray-600">
                of goal{pct >= 200 ? ` (${ratio.toFixed(1)}×)` : ""}
              </div>
            </div>
          </div>

          {/* Progress bar with milestone markers */}
          <div className="relative mt-4">
            <div className="absolute inset-0 flex justify-between px-1 pt-5">
              {achievements.map((achievement, index) => {
                const position = (achievement.minutes * 60) / safeGoal * 100;
                if (position > 100) return null;
                return (
                  <div 
                    key={index}
                    className="relative"
                    style={{ left: `${position}%` }}
                  >
                    <div className="absolute -top-2 -ml-2 flex flex-col items-center">
                      <div className="h-3 w-0.5 bg-gray-300"></div>
                      <div className="mt-1 text-xs font-medium text-gray-500">
                        {achievement.minutes}'
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="h-4 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className={`h-full ${motivation.gradient} transition-all duration-700 ease-out`}
                style={{ width: `${barPct}%` }}
              />
            </div>
          </div>

          {/* Achievement suggestions */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            {achievements.map((achievement, index) => {
              const achieved = safeDone >= achievement.minutes * 60;
              const nextAchievement = !achieved && (
                index === 0 || 
                safeDone >= achievements[index - 1].minutes * 60
              );
              
              return (
                <div
                  key={index}
                  className={`rounded-xl p-3 text-center transition-all ${
                    achieved
                      ? 'bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100'
                      : nextAchievement
                      ? 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100'
                      : 'bg-gray-50 border border-gray-100'
                  }`}
                >
                  <div className="text-lg">{achievement.icon}</div>
                  <div className={`text-xs font-semibold mt-1 ${
                    achieved ? 'text-emerald-700' : nextAchievement ? 'text-amber-700' : 'text-gray-500'
                  }`}>
                    {achievement.minutes} min
                  </div>
                  <div className={`text-xs mt-0.5 ${
                    achieved ? 'text-emerald-600' : nextAchievement ? 'text-amber-600' : 'text-gray-400'
                  }`}>
                    {achievement.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Next milestone info */}
        {ratio < 1 && (
          <div className="mt-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border border-blue-100">
            <div className="flex items-center gap-2">
              <span className="text-lg">🎯</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">
                  Keep going to reach your goal!
                </p>
                <p className="text-xs text-blue-600 mt-0.5">
                  {formatMMSS(safeGoal - safeDone)} more to go
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="mt-6 flex justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 transition-all hover:bg-gray-50 hover:shadow-sm"
          >
            View Details
          </button>
          <button
            type="button"
            onClick={onClose}
            className={`flex-1 rounded-xl ${motivation.gradient} px-4 py-3 font-medium text-white transition-all hover:shadow-lg hover:shadow-black/10 active:scale-95`}
          >
            Continue Practice
          </button>
        </div>
      </div>
    </div>
  );
}